import React, { useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { Field } from "formik";
import Select from "react-dropdown-select";

import { Label, TextArea, DropSelect } from "modules/primitives";
import styles from "./styles.module.scss";
import LoadingComponent from "modules/primitives/loading/loading.component";
import { getPlantsAndAreas } from "modules/admin/adminPlantsAndAreas/adminPlantsAndAreas.actions";
import ReplicationProjectThemes from "modules/projects/project/replicateProject/replicationProjectThemes";
import { make4NextQuartersFromToday } from "../../../../common/helper";
import PlantSingleSelect from "../../../../common/components/plantSingleselect";
import UserMultiSelectSearch from "../../../../common/components/usersSearch";

const stages = make4NextQuartersFromToday();

const FordEuropeTemplate = ({ formikprops, proposalData, projects }) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const {
    adminPlantsAndAreasReducer: { plantsAndAreas },
    authReducer: { session },
  } = useSelector((state) => state);

  useEffect(() => {
    if (!plantsAndAreas.length) {
      dispatch(
        getPlantsAndAreas({
          enqueueSnackbar,
        }),
      );
    }
  }, [plantsAndAreas]);

  const getPlantsAsOptions = useCallback((filterBy) => {
    const makePlants = plantsAndAreas.map(plant => ({
      id: plant.id,
      name: plant.attributes.name,
      areas: plant.attributes.areas,
    }));
    const { values } = formikprops;
    const existingValue = values[filterBy];

    if (filterBy === 'selectedPlant' && existingValue?.id) {
      return makePlants.filter(plant => existingValue.id !== plant.id);
    }

    if (filterBy === 'selectedPlant1' && existingValue?.length) {
      return makePlants.filter(plant => !existingValue.some(p => p.id === plant.id));
    }

    return makePlants;
  }, [plantsAndAreas, formikprops.values]);

  const getFPSField = useMemo(() => {
    if (!proposalData) {
      return null;
    }

    const getFPSCategory = proposalData?.project_idea_modal?.find(f => f.field_name === 'FPS Category');
    const formattedOptions = getFPSCategory?.option_values?.map((option) => ({
      label: option,
      value: option,
    })) || [];
    const values = formikprops.values?.fps_category?.length
      ? formikprops.values?.fps_category
        .map(elem => formattedOptions.find(el => el.value === (elem?.label || elem)))
      : [];

    if (getFPSCategory) {
      return (
        <div className="mb-3">
          <Label>{getFPSCategory.field_name}</Label>
          <Select
            name="fps_category"
            multi={getFPSCategory?.multiselect}
            options={formattedOptions}
            onChange={(val) => {
              formikprops.setFieldValue('fps_category', val);
            }}
            values={values}
          />
        </div>
      )
    }
  }, [proposalData]);

  const getSkillsTeamField = useMemo(() => {
    if (!proposalData) {
      return null;
    }

    const skillsField = proposalData?.project_idea_modal?.find(f => f.field_name === 'Skills Team');
    const formattedOptions = skillsField?.option_values?.map((option) => ({
      label: option,
      value: option,
    })) || [];
    const values = formikprops.values?.skills_team?.length
      ? formikprops.values?.skills_team
        .map(elem => formattedOptions.find(el => el.value === (elem?.label || elem)))
      : [];

    return (
      <div className="mb-3">
        <Label>Skills Team</Label>
        <Select
          name="skills_team"
          multi={skillsField?.multiselect}
          options={formattedOptions}
          onChange={(val) => {
            formikprops.setFieldValue('skills_team', val);
          }}
          values={values}
        />
      </div>
    )
  }, [proposalData]);

  const getWhereIdeaComeFromField = useMemo(() => {
    if (!proposalData) {
      return null;
    }

    const field = proposalData?.project_idea_modal?.find(f => f.field_name === 'Where did this Idea come from?');
    const formattedOptions = field?.option_values?.map((option) => ({
      label: option,
      value: option,
    })) || [];
    const values = formikprops.values?.where_did_this_idea_come_from?.length
      ? formikprops.values?.where_did_this_idea_come_from
        .map(elem => formattedOptions.find(el => el.value === (elem?.label || elem)))
      : [];

    return (
      <div className="mb-3">
        <Label>Where did this Idea come from?</Label>
        <Select
          name="where_did_this_idea_come_from"
          multi={field?.multiselect}
          options={formattedOptions}
          onChange={(val) => {
            formikprops.setFieldValue('where_did_this_idea_come_from', val);
          }}
          values={values}
        />
      </div>
    )
  }, [proposalData]);

  return (
    <div className="d-flex flex-column w-100">
      <div className="mb-3">
        <ReplicationProjectThemes
          name="Innovation Pillar"
          handleSelect={(themes) => {
            formikprops.setFieldValue("themes", themes);
          }}
          themes={formikprops.values.themes || []}
        />
      </div>
      <div className="mb-3">
        <PlantSingleSelect formikprops={formikprops} plants={getPlantsAsOptions("selectedPlant1")} />
        {
          !formikprops?.values?.selectedPlant?.id && (
            <div className={styles.invalidFeedback}>Plant is required</div>
          )
        }
      </div>
      <div className="d-flex flex-column w-100 mb-3">
        <Label>Is this a Replication?</Label>
        <div className="d-flex align-items-center w-100 ml-5">
          <div className="w-25 position-relative">
            <label className={styles.formCheckLabel} htmlFor="originalOrReplicationYes">
              <Field
                id="originalOrReplicationYes"
                name="originalOrReplicationYes"
                type="radio"
                className={styles.formCheckInput}
                checked={formikprops.values.isReplication}
                onChange={() => formikprops.setFieldValue("isReplication", true)}
              />
              Yes
            </label>
          </div>
          <div className="w-25 position-relative">
            <label className={styles.formCheckLabel} htmlFor="originalOrReplicationNo">
              <Field
                id="originalOrReplicationNo"
                name="originalOrReplicationNo"
                type="radio"
                className={styles.formCheckInput}
                checked={!formikprops.values.isReplication}
                onChange={() => formikprops.setFieldValue("isReplication", false)}
              />
              No
            </label>
          </div>
        </div>
      </div>
      {
        formikprops.values.isReplication && (
          <div className="mb-3">
            {
              !projects ? (
                <LoadingComponent customText="Getting Projects..." />
              ) : (
                <>
                  <Label>Select Original Project</Label>
                  <DropSelect
                    onChange={(value) => {
                      formikprops.setFieldValue("project_id", value.val);
                    }}
                    placeholder="Select Original Project"
                    labelField="name"
                    valueField="id"
                    searchBy="name"
                    options={projects || []}
                    values={formikprops.values.project_id && projects
                      ? projects?.filter(p => p.id === formikprops.values.project_id)
                      : []
                    }
                  />
                </>
              )
            }
          </div>
        )
      }
      <div className="mb-2">
        <Label>Description</Label>
        <TextArea
          type="text"
          name="description"
          placeholder="Describe the problem you are trying to solve, the project goal, or description."
          className="w-100"
          formProps={formikprops}
          rows={4}
        />
      </div>
      <div className="mb-2">
        <Label>Problem Statement</Label>
        <TextArea
          type="text"
          name="problem_statement"
          placeholder="Problem Statement"
          className="w-100"
          formProps={formikprops}
          rows={4}
        />
      </div>
      <div className="mb-2">
        <Label>Potential Benefits</Label>
        <TextArea
          type="text"
          name="potential_benefits"
          placeholder="Potential Benefits"
          className="w-100"
          formProps={formikprops}
          rows={4}
        />
      </div>
      {getSkillsTeamField}
      {getWhereIdeaComeFromField}
      {getFPSField}
      <div className="mb-3">
        <Label>Next Stage Change</Label>
        <Select
          label="Next Stage Change"
          placeholder="Next Stage Change"
          labelField="name"
          valueField="id"
          name="next_stage_change"
          multi={false}
          options={stages}
          onChange={(val) => {
            formikprops.setFieldValue("next_stage_change", val[0]);
          }}
          values={formikprops.values.next_stage_change ? [formikprops.values.next_stage_change] : []}
        />
      </div>
      <div className="mb-3">
        <Label>Select Project Owner</Label>
        <UserMultiSelectSearch
          multi={false}
          placeholder="Search for project owner"
          labelField="full_name"
          valueField="id"
          searchBy="full_name"
          hasAll={false}
          onChange={(value) => {
            formikprops.setFieldValue("project_owners", value);
          }}
          preselectCurrentUser
        />
        {formikprops.errors["project_owners"] && (
          <div className={styles.invalidFeedback}>{formikprops.errors['project_owners']}</div>
        )}
      </div>
    </div>
  );
};

export default FordEuropeTemplate;
